/**
 * List Deals Handler
 * Query handler for listing deals with filters
 */

import type { ToolExecutionContext } from '../../types';
import { QueryHandlerBase } from '../base';
import { createUserGraphQLClient } from '../../utils/graphql-client';

/**
 * List deals handler
 * Fetches deals with optional filtering and ordering
 */
export class ListDealsHandler extends QueryHandlerBase<any> {
  id = 'list_deals';

  schema = {
    type: 'object',
    properties: {
      limit: {
        type: 'number',
        description: 'Maximum number of deals to return (default: 20, max: 100)',
      },
      stage: {
        type: 'array',
        items: { type: 'string' },
        description: 'Filter by deal stage',
      },
      company_id: {
        type: 'string',
        description: 'Filter by company ID',
      },
      probability_min: {
        type: 'number',
        description: 'Minimum close probability (0-100)',
      },
      probability_max: {
        type: 'number',
        description: 'Maximum close probability (0-100)',
      },
      close_date_from: {
        type: 'string',
        description: 'Filter deals with close date from this date (ISO format)',
      },
      close_date_to: {
        type: 'string',
        description: 'Filter deals with close date until this date (ISO format)',
      },
      is_recurring: {
        type: 'boolean',
        description: 'Filter by recurring deals only',
      },
      is_committed: {
        type: 'boolean',
        description: 'Filter by committed deals only',
      },
      orderBy: {
        type: 'string',
        enum: ['created_at', 'updated_at', 'amount', 'close_date', 'close_probability'],
        description: 'Field to order results by (default: created_at)',
      },
    },
    required: [],
  };

  async execute(params: Record<string, any>, context?: ToolExecutionContext): Promise<any> {
    if (!context?.userToken) {
      throw new Error('User token is required for deal listing');
    }

    try {
      const { GetDealssDocument } = await import('@/src/gql/graphql');
      const client = createUserGraphQLClient(context.userToken);

      const limit = Math.min(params.limit || 20, 100);
      const orderBy = params.orderBy || 'created_at';

      // Build filter object
      const filter: any = {};

      if (params.stage && params.stage.length > 0) {
        filter.stage = { in: params.stage };
      }

      if (params.company_id) {
        filter.company_id = { eq: params.company_id };
      }

      if (params.probability_min !== undefined || params.probability_max !== undefined) {
        filter.close_probability = {};
        if (params.probability_min !== undefined) {
          filter.close_probability.gte = params.probability_min;
        }
        if (params.probability_max !== undefined) {
          filter.close_probability.lte = params.probability_max;
        }
      }

      if (params.close_date_from || params.close_date_to) {
        filter.close_date = {};
        if (params.close_date_from) {
          filter.close_date.gte = params.close_date_from;
        }
        if (params.close_date_to) {
          filter.close_date.lte = params.close_date_to;
        }
      }

      if (params.is_recurring !== undefined) {
        filter.is_recurring = { eq: params.is_recurring };
      }

      if (params.is_committed !== undefined) {
        filter.is_committed = { eq: params.is_committed };
      }

      const result = await client.query({
        query: GetDealssDocument,
        variables: {
          first: limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          orderBy: [{ [orderBy]: 'DescNullsLast' }],
        },
        fetchPolicy: 'network-only',
      });

      if (result.error) {
        throw new Error(`GraphQL error: ${result.error.message}`);
      }

      const data = result.data as any;
      const deals = data?.dealsCollection?.edges?.map((edge: any) => edge?.node).filter((node: any) => node != null) || [];

      return {
        deals: deals.map((d: any) => ({
          ...d,
          viewInApp: `[${d.title}](/deals/${d.id})`,
        })),
        count: deals.length,
        limit,
      };
    } catch (error) {
      throw new Error(`Deal listing failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}
